import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

import { ActiveRangeFilter, RangeFilter } from './typing/range-filter-types.js'
import { style } from './range-filter.style.js'

import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { renderAccordionIcons } from 'components/discovery/plp/filters/plp-filters/accordion-icons.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'

import { CSERangeSlider, CSETextInput } from '@cloudsuite/mosaic-elements'

export type RangeFilterAction = 'filter' | 'clear'

export interface RangeFilterEventDetail {
  action: RangeFilterAction
  minValue: number
  maxValue: number
  filter: RangeFilter
  active?: ActiveRangeFilter
}

export class RangeFilterOnFilterEvent extends CustomEvent<RangeFilterEventDetail> {
  static type = 'filter-on-filter' as const

  constructor(detail: RangeFilterEventDetail) {
    super(RangeFilterOnFilterEvent.type, {
      bubbles: true,
      composed: true,
      detail,
    })
  }
}

const RangeFilterDefaults: RangeFilter = {
  count: 1,
  description: '',
  graph: false,
  id: 1,
  max: '100.00',
  min: '1.00',
  name: '',
  type: '',
  values: [],
  uom: {
    name: '',
    symbol: ''
  }
}

@customElement('csc-range-filter')
export class CSCRangeFilter extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Object, attribute: 'selected-range' })
  selectedRange!: ActiveRangeFilter

  @property({ type: Object, attribute: 'range' })
  range: RangeFilter = RangeFilterDefaults

  @property({ type: String, attribute: 'heading-variant' })
  headingVariant: string = 'heading-3'

  @property({ type: String, attribute: 'accordion-size' })
  accordionSize = 'md'

  @state()
  _minValue: number = 0.00

  @state()
  _maxValue: number = 0.00

  private handleRangeSliderChange(event: Event) {
    const element: CSERangeSlider = event.currentTarget as CSERangeSlider

    this._minValue = Math.max(parseFloat(this.range.min), Number(element.startValue))
    this._maxValue = Math.min(parseFloat(this.range.max), Number(element.stopValue))

    this.dispatchFilterEvent('filter')
  }

  private handleTextChange(event: Event) {
    const element: CSETextInput = event.currentTarget as CSETextInput
    const numberValue = Number(element.value)

    if (element.id === 'range-filter-min-input') {
      this._minValue = Math.max(parseFloat(this.range.min), numberValue)
    } else {
      this._maxValue = Math.min(parseFloat(this.range.max), numberValue)
    }

    this.dispatchFilterEvent('filter')
  }

  override connectedCallback() {
    super.connectedCallback()

    this._minValue = this.selectedRange?.min ? this.selectedRange.min : parseFloat(this.range.min)
    this._maxValue = this.selectedRange?.max ? this.selectedRange.max : parseFloat(this.range.max)
  }

  private dispatchFilterEvent(action: RangeFilterAction) {
    const detail: RangeFilterEventDetail = {
      action,
      minValue: this._minValue,
      maxValue: this._maxValue,
      filter: this.range,
      active: this.selectedRange,
    }

    this.dispatchEvent(new RangeFilterOnFilterEvent(detail))
  }

  private handleClearClick(event: Event) {
    event.preventDefault()
    event.stopPropagation()

    this._minValue = parseFloat(this.range.min)
    this._maxValue = parseFloat(this.range.max)

    this.dispatchFilterEvent('clear')
  }

  override render() {
    return html`
      <cse-accordion open variant="panel" size=${this.accordionSize}>
        ${renderAccordionIcons(this.accordionSize)}
        <cse-text slot="title" as="h3" variant=${this.headingVariant}>
          ${this.range.name}
        </cse-text>
        <div class="range-filter" slot="content">
          <cse-range-slider
            max="${this.range.max}"
            min="${this.range.min}"
            start-value="${this._minValue}"
            stop-value="${this._maxValue}"
            @change="${this.handleRangeSliderChange}"
          ></cse-range-slider>
          <div class="range-filter-inputs">
            <cse-text-input
              id="range-filter-min-input"
              min="${this.range.min}"
              .value="${this._minValue}"
              @change="${this.handleTextChange}"
              type="number"
            >
            ${this.range.uom && this.range.uom.symbol.length > 0
              ? html`<cse-text slot="suffix" variant="caption-l">${this.range.uom.symbol}</cse-text>`
              : nothing
            }
            </cse-text-input>
            <cse-text id="range-filter-to">
              ${t('range_filter_to_text', { ns: 'csc-range-filter', default: 'To' })}
            </cse-text>
            <cse-text-input
              id="range-filter-max-input"
              max="${this.range.max}"
              .value="${this._maxValue}"
              @change="${this.handleTextChange}"
              type="number"
            >
            ${this.range.uom && this.range.uom.symbol.length > 0
              ? html`<cse-text slot="suffix" variant="caption-l">${this.range.uom.symbol}</cse-text>`
              : nothing
            }
            </cse-text-input>
          </div>
          <div class="range-filter-buttons">
            <cse-link
              class="range-filter-clear"
              @click=${this.handleClearClick}
            >
              ${t('range_filter_clear_link_text', { ns: 'csc-range-filter', default: 'Clear' })}
            </cse-link>
          </div>
        </div>
      </cse-accordion>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-range-filter': CSCRangeFilter,
  }
}
